/**
 * 감사 이벤트 카탈로그 (PRD2 T-0.8, §14.4).
 * 이름은 category.action 이고, 라벨이 없으면 등록되지 않는다.
 */

export type AuditSeverity = "info" | "warning" | "critical";

export type AuditEvent = {
  id: string;
  label: string;
  severity: AuditSeverity;
  notify: boolean;
};

const ID_PATTERN = /^[a-z]+\.[a-z_]+$/;

/** 라벨이 비거나 이름이 규칙 밖이면 던진다. 카탈로그에 넣는 유일한 길이다. */
export function defineAuditEvent(event: AuditEvent): AuditEvent {
  if (!event.label.trim()) throw new Error(`라벨 없는 이벤트: ${event.id}`);
  if (!ID_PATTERN.test(event.id)) throw new Error(`이름 형식: ${event.id}`);
  return { ...event, label: event.label.trim() };
}

function event(id: string, label: string, severity: AuditSeverity, notify: boolean): AuditEvent {
  return defineAuditEvent({ id, label, severity, notify });
}

export const AUDIT_EVENTS: readonly AuditEvent[] = [
  event("auth.signed_in", "로그인", "info", false),
  event("auth.signed_out", "로그아웃", "info", false),
  event("auth.sign_in_failed", "로그인 실패", "warning", false),
  event("auth.provider_unlinked", "로그인 연결 해제", "warning", true),
  event("access.denied", "접근 거절", "info", false),
  event("account.deletion_requested", "계정 삭제 요청", "critical", true),
  event("member.invited", "멤버 초대", "info", true),
  event("member.removed", "멤버 제거", "warning", true),
  event("member.left", "멤버 나가기", "info", true),
  event("member.flag_changed", "능력 변경", "warning", true),
  event("plan.created", "플랜 만들기", "info", false),
  event("plan.archived", "플랜 아카이브", "warning", true),
  event("plan.unarchived", "플랜 복구", "info", true),
  event("plan.trashed", "플랜 삭제", "critical", true),
  event("plan.restored", "휴지통 복구", "info", true),
  event("plan.owner_transferred", "플랜 소유권 이전", "critical", true),
  event("plan.admin_access_granted", "관리자 접근", "critical", true),
  event("workspace.archived", "워크스페이스 아카이브", "critical", true),
  event("workspace.trashed", "워크스페이스 삭제", "critical", true),
  event("workspace.owner_transferred", "워크스페이스 소유권 이전", "critical", true),
  event("share.created", "공유 만들기", "info", false),
  event("share.updated", "공유 수정", "info", false),
  event("share.duplicated", "공유 복제", "info", false),
  event("share.published", "공유 발행", "info", false),
  event("share.paused", "공유 일시 중지", "warning", false),
  event("share.resumed", "공유 재개", "info", false),
  event("share.extended", "공유 연장", "info", false),
  event("share.rotated", "공유 회전", "warning", false),
  event("share.revoked", "공유 철회", "warning", true),
  event("share.transferred", "공유 인수", "info", true),
  event("share.bulk_paused", "공유 일괄 중지", "warning", true),
  event("share.viewed", "공유 열람", "info", false),
  event("share.gate_failed", "공유 입장 실패", "warning", false),
  event("share.denied", "공유 거절", "info", false),
  event("share.downloaded", "공유 다운로드", "info", false),
  event("share.limit_reached", "공유 횟수 소진", "warning", false),
  event("share.policy_violation", "공유 규정 밖", "warning", true),
  event("token.bulk_revoked", "토큰 일괄 폐기", "warning", true),
  event("baseline.deleted", "기준선 삭제", "warning", true),
  event("baseline.replaced", "기준선 교체", "warning", true),
  event("policy.retention_shortened", "보존 기간 단축", "critical", true),
  event("policy.relaxed", "정책 완화", "critical", true),
];

const byId = new Map(AUDIT_EVENTS.map((item) => [item.id, item]));

export function auditEvent(id: string): AuditEvent | undefined {
  return byId.get(id);
}
